// frontend/src/pages/BeginningBalances/BeginningBalances.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiPlus,
  FiRefreshCw,
  FiRotateCcw,
  FiSave,
  FiSearch,
  FiTrash2,
  FiUploadCloud,
  FiX,
} from "react-icons/fi";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./BeginningBalances.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const today = () => new Date().toISOString().slice(0, 10);

const currentFiscalYear = () => {
  const year = new Date().getFullYear();
  return `${year}-${year + 1}`;
};

const makeLine = () => ({
  row_key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  account_id: "",
  customer_id: "",
  supplier_id: "",
  debit: "",
  credit: "",
  description: "",
});

const createEmptyForm = (referenceNo = "", currencyCode = "USD") => ({
  _id: null,
  version: 1,
  transaction_no: referenceNo,
  transaction_date: today(),
  fiscal_year: currentFiscalYear(),
  reference_no: referenceNo,
  description: `Opening balances as at ${today()}`,
  currency_code: currencyCode,
  status: "draft",
  lines: [makeLine(), makeLine()],
});

// ✅ Robust helper to extract a 3‑letter currency code from company object
const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }

  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }

  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }

  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }

  return currency;
};

const unwrapError = (error, fallback) => {
  const message = error?.response?.data?.message || error?.message || fallback;
  const details = error?.response?.data?.details;
  if (Array.isArray(details) && details.length) {
    return `${message}: ${details.join(", ")}`;
  }
  return message;
};

const getAccountType = (account) => account?.accounting_type || {};
const getNormalBalance = (account) => {
  if (!account) return "";
  const accountingType = getAccountType(account);
  const explicitNormal = String(accountingType.normal_balance || "").toUpperCase();
  if (["DR", "CR"].includes(explicitNormal)) return explicitNormal;
  const mainType = accountingType.type || account.type;
  return ["Asset", "Expense"].includes(mainType) ? "DR" : "CR";
};

const accountLabel = (account) =>
  account ? `${account.account_code} — ${account.account_name}` : "";
const customerLabel = (customer) =>
  customer ? `${customer.customer_name} (${customer.customer_id || ""})` : "";
const supplierLabel = (supplier) =>
  supplier ? `${supplier.supplier_name} (${supplier.supplier_id || ""})` : "";

// ========================== ACCOUNT COMBOBOX ==========================
function AccountCombobox({
  value,
  accounts,
  unavailableIds,
  disabled,
  listId,
  onChange,
}) {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const selectedAccount = accounts.find(
    (account) => String(account._id) === String(value),
  );
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(accountLabel(selectedAccount));
  const [activeIndex, setActiveIndex] = useState(0);
  const [menuStyle, setMenuStyle] = useState({});

  useEffect(() => {
    if (!open) setQuery(accountLabel(selectedAccount));
  }, [open, selectedAccount]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
        setQuery(accountLabel(selectedAccount));
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [selectedAccount]);

  const availableAccounts = useMemo(
    () =>
      accounts.filter(
        (account) =>
          !unavailableIds.has(String(account._id)) ||
          String(account._id) === String(value),
      ),
    [accounts, unavailableIds, value],
  );

  const filteredAccounts = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term || query === accountLabel(selectedAccount)) return availableAccounts;
    return availableAccounts.filter((account) => {
      const accountingType = getAccountType(account);
      const searchable = [
        account.account_code,
        account.account_name,
        accountingType.type || account.type,
        accountingType.sub_type,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return searchable.includes(term);
    });
  }, [availableAccounts, query, selectedAccount]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  useEffect(() => {
    if (!open || !inputRef.current) return undefined;
    const updateMenuPosition = () => {
      const rect = inputRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const width = Math.min(Math.max(rect.width, 380), viewportWidth - 24);
      const left = Math.min(Math.max(rect.left, 12), viewportWidth - width - 12);
      const spaceBelow = viewportHeight - rect.bottom - 12;
      const spaceAbove = rect.top - 12;
      if (spaceBelow >= 220 || spaceBelow >= spaceAbove) {
        setMenuStyle({
          left,
          top: rect.bottom + 6,
          width,
          maxHeight: Math.max(160, Math.min(340, spaceBelow)),
        });
      } else {
        setMenuStyle({
          left,
          bottom: viewportHeight - rect.top + 6,
          width,
          maxHeight: Math.max(160, Math.min(340, spaceAbove)),
        });
      }
    };
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open]);

  const selectAccount = (account) => {
    onChange(String(account._id));
    setQuery(accountLabel(account));
    setOpen(false);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) =>
        Math.min(current + 1, Math.max(filteredAccounts.length - 1, 0)),
      );
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => Math.max(current - 1, 0));
      return;
    }
    if (event.key === "Enter" && open && filteredAccounts[activeIndex]) {
      event.preventDefault();
      selectAccount(filteredAccounts[activeIndex]);
      return;
    }
    if (event.key === "Escape") {
      setOpen(false);
      setQuery(accountLabel(selectedAccount));
    }
  };

  return (
    <div className="bb-account-combobox" ref={rootRef}>
      <FiSearch className="bb-account-search-icon" aria-hidden="true" />
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls={listId}
        value={query}
        disabled={disabled}
        placeholder="Search code or account name"
        onFocus={(event) => {
          setOpen(true);
          event.target.select();
        }}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onKeyDown={handleKeyDown}
      />
      {open && !disabled ? (
        <div
          className="bb-account-options"
          id={listId}
          role="listbox"
          style={menuStyle}
        >
          {filteredAccounts.length ? (
            filteredAccounts.map((account, index) => {
              const accountingType = getAccountType(account);
              const normalBalance = getNormalBalance(account);
              const isSelected = String(account._id) === String(value);
              return (
                <button
                  key={account._id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`bb-account-option ${index === activeIndex ? "bb-account-option-active" : ""}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectAccount(account)}
                >
                  <span>
                    <strong>{account.account_code}</strong>
                    <span>{account.account_name}</span>
                  </span>
                  <small>
                    {accountingType.type || account.type} / {accountingType.sub_type || account.type}
                    <b>{normalBalance}</b>
                  </small>
                </button>
              );
            })
          ) : (
            <div className="bb-account-option-empty">No matching posting account</div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ========================== CUSTOMER COMBOBOX ==========================
function CustomerCombobox({
  value,
  customers,
  excludedIds = new Set(),
  disabled,
  listId,
  onChange,
}) {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const selectedCustomer = customers.find(
    (customer) => String(customer._id) === String(value),
  );
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(customerLabel(selectedCustomer));
  const [activeIndex, setActiveIndex] = useState(0);
  const [menuStyle, setMenuStyle] = useState({});

  useEffect(() => {
    if (!open) setQuery(customerLabel(selectedCustomer));
  }, [open, selectedCustomer]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
        setQuery(customerLabel(selectedCustomer));
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [selectedCustomer]);

  const availableCustomers = useMemo(
    () =>
      customers.filter(
        (customer) =>
          !excludedIds.has(String(customer._id)) ||
          String(customer._id) === String(value),
      ),
    [customers, excludedIds, value],
  );

  const filteredCustomers = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term || query === customerLabel(selectedCustomer)) return availableCustomers;
    return availableCustomers.filter((customer) => {
      const searchable = [
        customer.customer_name,
        String(customer.customer_id || ""),
        customer.phone,
        customer.email,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return searchable.includes(term);
    });
  }, [availableCustomers, query, selectedCustomer]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  useEffect(() => {
    if (!open || !inputRef.current) return undefined;
    const updateMenuPosition = () => {
      const rect = inputRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const width = Math.min(Math.max(rect.width, 380), viewportWidth - 24);
      const left = Math.min(Math.max(rect.left, 12), viewportWidth - width - 12);
      const spaceBelow = viewportHeight - rect.bottom - 12;
      const spaceAbove = rect.top - 12;
      if (spaceBelow >= 220 || spaceBelow >= spaceAbove) {
        setMenuStyle({
          left,
          top: rect.bottom + 6,
          width,
          maxHeight: Math.max(160, Math.min(340, spaceBelow)),
        });
      } else {
        setMenuStyle({
          left,
          bottom: viewportHeight - rect.top + 6,
          width,
          maxHeight: Math.max(160, Math.min(340, spaceAbove)),
        });
      }
    };
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open]);

  const selectCustomer = (customer) => {
    onChange(String(customer._id));
    setQuery(customerLabel(customer));
    setOpen(false);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) =>
        Math.min(current + 1, Math.max(filteredCustomers.length - 1, 0)),
      );
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => Math.max(current - 1, 0));
      return;
    }
    if (event.key === "Enter" && open && filteredCustomers[activeIndex]) {
      event.preventDefault();
      selectCustomer(filteredCustomers[activeIndex]);
      return;
    }
    if (event.key === "Escape") {
      setOpen(false);
      setQuery(customerLabel(selectedCustomer));
    }
  };

  return (
    <div className="bb-customer-combobox" ref={rootRef}>
      <FiSearch className="bb-account-search-icon" aria-hidden="true" />
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls={listId}
        value={query}
        disabled={disabled}
        placeholder="Search customer"
        onFocus={(event) => {
          setOpen(true);
          event.target.select();
        }}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onKeyDown={handleKeyDown}
      />
      {open && !disabled ? (
        <div
          className="bb-account-options"
          id={listId}
          role="listbox"
          style={menuStyle}
        >
          {filteredCustomers.length ? (
            filteredCustomers.map((customer, index) => {
              const isSelected = String(customer._id) === String(value);
              return (
                <button
                  key={customer._id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`bb-account-option ${index === activeIndex ? "bb-account-option-active" : ""}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectCustomer(customer)}
                >
                  <span>
                    <strong>{customer.customer_name}</strong>
                    <span>CUS-{String(customer.customer_id || "").padStart(4, "0")}</span>
                  </span>
                  <small>
                    {customer.customer_type || "Regular"} / {customer.phone || "No phone"}
                  </small>
                </button>
              );
            })
          ) : (
            <div className="bb-account-option-empty">No matching customer</div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ========================== SUPPLIER COMBOBOX ==========================
function SupplierCombobox({
  value,
  suppliers,
  excludedIds = new Set(),
  disabled,
  listId,
  onChange,
}) {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const selectedSupplier = suppliers.find(
    (supplier) => String(supplier._id) === String(value),
  );
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(supplierLabel(selectedSupplier));
  const [activeIndex, setActiveIndex] = useState(0);
  const [menuStyle, setMenuStyle] = useState({});

  useEffect(() => {
    if (!open) setQuery(supplierLabel(selectedSupplier));
  }, [open, selectedSupplier]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
        setQuery(supplierLabel(selectedSupplier));
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [selectedSupplier]);

  const availableSuppliers = useMemo(
    () =>
      suppliers.filter(
        (supplier) =>
          !excludedIds.has(String(supplier._id)) ||
          String(supplier._id) === String(value),
      ),
    [suppliers, excludedIds, value],
  );

  const filteredSuppliers = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term || query === supplierLabel(selectedSupplier)) return availableSuppliers;
    return availableSuppliers.filter((supplier) => {
      const searchable = [
        supplier.supplier_name,
        String(supplier.supplier_id || ""),
        supplier.phone,
        supplier.email,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return searchable.includes(term);
    });
  }, [availableSuppliers, query, selectedSupplier]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  useEffect(() => {
    if (!open || !inputRef.current) return undefined;
    const updateMenuPosition = () => {
      const rect = inputRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const width = Math.min(Math.max(rect.width, 380), viewportWidth - 24);
      const left = Math.min(Math.max(rect.left, 12), viewportWidth - width - 12);
      const spaceBelow = viewportHeight - rect.bottom - 12;
      const spaceAbove = rect.top - 12;
      if (spaceBelow >= 220 || spaceBelow >= spaceAbove) {
        setMenuStyle({
          left,
          top: rect.bottom + 6,
          width,
          maxHeight: Math.max(160, Math.min(340, spaceBelow)),
        });
      } else {
        setMenuStyle({
          left,
          bottom: viewportHeight - rect.top + 6,
          width,
          maxHeight: Math.max(160, Math.min(340, spaceAbove)),
        });
      }
    };
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open]);

  const selectSupplier = (supplier) => {
    onChange(String(supplier._id));
    setQuery(supplierLabel(supplier));
    setOpen(false);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) =>
        Math.min(current + 1, Math.max(filteredSuppliers.length - 1, 0)),
      );
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => Math.max(current - 1, 0));
      return;
    }
    if (event.key === "Enter" && open && filteredSuppliers[activeIndex]) {
      event.preventDefault();
      selectSupplier(filteredSuppliers[activeIndex]);
      return;
    }
    if (event.key === "Escape") {
      setOpen(false);
      setQuery(supplierLabel(selectedSupplier));
    }
  };

  return (
    <div className="bb-supplier-combobox" ref={rootRef}>
      <FiSearch className="bb-account-search-icon" aria-hidden="true" />
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls={listId}
        value={query}
        disabled={disabled}
        placeholder="Search supplier"
        onFocus={(event) => {
          setOpen(true);
          event.target.select();
        }}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onKeyDown={handleKeyDown}
      />
      {open && !disabled ? (
        <div
          className="bb-account-options"
          id={listId}
          role="listbox"
          style={menuStyle}
        >
          {filteredSuppliers.length ? (
            filteredSuppliers.map((supplier, index) => {
              const isSelected = String(supplier._id) === String(value);
              return (
                <button
                  key={supplier._id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`bb-account-option ${index === activeIndex ? "bb-account-option-active" : ""}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectSupplier(supplier)}
                >
                  <span>
                    <strong>{supplier.supplier_name}</strong>
                    <span>SUP-{String(supplier.supplier_id || "").padStart(4, "0")}</span>
                  </span>
                  <small>
                    {supplier.phone || "No phone"}
                  </small>
                </button>
              );
            })
          ) : (
            <div className="bb-account-option-empty">No matching supplier</div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ========================== MAIN COMPONENT ==========================
function BeginningBalances() {
  const { company, getCompanyId, userType } = useAuth();
  const { formatCurrency } = useCurrency();

  // ✅ Get company currency safely
  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  const companyId = getCompanyId?.() || company?._id || company?.id || "";
  const companyParams = useMemo(
    () => (userType === "system" && companyId ? { company_id: companyId } : {}),
    [companyId, userType],
  );

  const [accounts, setAccounts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [form, setForm] = useState(createEmptyForm("", companyCurrency));
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    draft: 0,
    posted: 0,
    reversed: 0,
  });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Customer add modal state
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerModalLineKey, setCustomerModalLineKey] = useState(null);
  const [newCustomerForm, setNewCustomerForm] = useState({
    customer_name: "",
    customer_type: "regular",
    phone: "",
    email: "",
    address: "",
    currency_code: companyCurrency,
    credit_limit: "0.00",
  });
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [customerErrors, setCustomerErrors] = useState({});

  // Supplier add modal state
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [supplierModalLineKey, setSupplierModalLineKey] = useState(null);
  const [newSupplierForm, setNewSupplierForm] = useState({
    supplier_name: "",
    phone: "",
    email: "",
    address: "",
    currency_code: companyCurrency,
    credit_limit: "0.00",
  });
  const [addingSupplier, setAddingSupplier] = useState(false);
  const [supplierErrors, setSupplierErrors] = useState({});

  // ✅ Format currency using company currency
  const formatAmount = useCallback((amount) => {
    return formatCurrency(amount, companyCurrency);
  }, [formatCurrency, companyCurrency]);

  const accountMap = useMemo(
    () => new Map(accounts.map((account) => [String(account._id), account])),
    [accounts],
  );

  // Filter out customer and supplier subledger accounts from the Account dropdown
  const generalLedgerAccounts = useMemo(() => {
    return accounts.filter((account) => {
      const role = account.system_account_role;
      if (role === "CUSTOMER" || role === "SUPPLIER") return false;
      if (account.customer_id || account.supplier_id) return false;
      return true;
    });
  }, [accounts]);

  const totals = useMemo(
    () =>
      form.lines.reduce(
        (result, line) => ({
          debit: result.debit + Number(line.debit || 0),
          credit: result.credit + Number(line.credit || 0),
        }),
        { debit: 0, credit: 0 },
      ),
    [form.lines],
  );

  const difference = Math.round((totals.debit - totals.credit) * 100) / 100;
  const isBalanced = totals.debit > 0 && Math.abs(difference) < 0.005;
  const isDraft = form.status === "draft";
  const isPosted = form.status === "posted";

  const showMessage = useCallback((type, text) => {
    setMessage({ type, text });
    window.setTimeout(() => setMessage(null), 6500);
  }, []);

  // Load customers and suppliers
  const loadCustomers = useCallback(async () => {
    try {
      const response = await axios.get(`${url}/api/customer/dropdown`, {
        params: { ...companyParams, include_inactive: false, limit: 500 },
        headers: getAuthHeaders(),
      });
      setCustomers(response.data?.data || []);
    } catch (error) {
      // silent
    }
  }, [companyParams]);

  const loadSuppliers = useCallback(async () => {
    try {
      const response = await axios.get(`${url}/api/supplier/dropdown`, {
        params: { ...companyParams, include_inactive: false, limit: 500 },
        headers: getAuthHeaders(),
      });
      setSuppliers(response.data?.data || []);
    } catch (error) {
      // silent
    }
  }, [companyParams]);

  const loadList = useCallback(
    async ({ searchValue = "", statusValue = "all" } = {}) => {
      const response = await axios.get(`${url}/api/accounting-opening-balances`, {
        params: {
          ...companyParams,
          search: searchValue.trim() || undefined,
          status: statusValue,
          limit: 100,
        },
        headers: getAuthHeaders(),
      });
      setEntries(response.data?.data || []);
      setSummary(
        response.data?.summary || {
          total: 0,
          draft: 0,
          posted: 0,
          reversed: 0,
        },
      );
    },
    [companyParams],
  );

  const refreshList = useCallback(
    () => loadList({ searchValue: search, statusValue: statusFilter }),
    [loadList, search, statusFilter],
  );

  const loadMetadata = useCallback(async () => {
    const response = await axios.get(
      `${url}/api/accounting-opening-balances/meta`,
      {
        params: companyParams,
        headers: getAuthHeaders(),
      }
    );
    const data = response.data?.data || {};
    setAccounts(data.accounts || []);
    setFiscalYears(data.fiscal_years || []);
    setForm((current) =>
      current._id
        ? current
        : {
            ...createEmptyForm(data.next_transaction_no || "", companyCurrency),
            currency_code: companyCurrency,
          },
    );
  }, [companyParams, companyCurrency]);

  const initialFilterEffectSkipped = useRef(false);

  const initialize = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadMetadata(), loadList(), loadCustomers(), loadSuppliers()]);
    } catch (error) {
      showMessage("error", unwrapError(error, "Failed to load beginning balances"));
    } finally {
      setLoading(false);
    }
  }, [loadList, loadMetadata, loadCustomers, loadSuppliers, showMessage]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (loading) return;
    if (!initialFilterEffectSkipped.current) {
      initialFilterEffectSkipped.current = true;
      return;
    }
    const timer = window.setTimeout(() => {
      refreshList().catch((error) =>
        showMessage("error", unwrapError(error, "Failed to refresh beginning balance entries")),
      );
    }, 250);
    return () => window.clearTimeout(timer);
  }, [refreshList, loading, showMessage]);

  const newEntry = async () => {
    try {
      const response = await axios.get(
        `${url}/api/accounting-opening-balances/meta`,
        {
          params: companyParams,
          headers: getAuthHeaders(),
        }
      );
      const data = response.data?.data || {};
      setForm(createEmptyForm(data.next_transaction_no || "", companyCurrency));
      setMessage(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      showMessage("error", unwrapError(error, "Failed to start a new entry"));
    }
  };

  const changeHeader = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const changeLine = (rowKey, field, value) => {
    setForm((current) => ({
      ...current,
      lines: current.lines.map((line) => {
        if (line.row_key !== rowKey) return line;

        if (field === "account_id") {
          const selectedAccount = accountMap.get(String(value));
          const normalBalance = getNormalBalance(selectedAccount);
          const customerId =
            selectedAccount?.system_account_role === "ACCOUNTS_RECEIVABLE"
              ? line.customer_id
              : "";
          const supplierId =
            selectedAccount?.system_account_role === "ACCOUNTS_PAYABLE"
              ? line.supplier_id
              : "";
          return {
            ...line,
            account_id: value,
            customer_id: customerId,
            supplier_id: supplierId,
            debit:
              normalBalance === "DR"
                ? line.debit
                : "",
            credit:
              normalBalance === "CR"
                ? line.credit
                : "",
          };
        }

        if (field === "debit") {
          const numValue = value === "" ? "" : Number(value);
          if (numValue !== "" && (isNaN(numValue) || numValue < 0)) return line;
          return {
            ...line,
            debit: value,
            credit: value !== "" && Number(value) > 0 ? "" : line.credit,
          };
        }

        if (field === "credit") {
          const numValue = value === "" ? "" : Number(value);
          if (numValue !== "" && (isNaN(numValue) || numValue < 0)) return line;
          return {
            ...line,
            credit: value,
            debit: value !== "" && Number(value) > 0 ? "" : line.debit,
          };
        }

        return { ...line, [field]: value };
      }),
    }));
  };

  const addLine = () => {
    setForm((current) => ({ ...current, lines: [...current.lines, makeLine()] }));
  };

  const removeLine = (rowKey) => {
    setForm((current) => {
      const remaining = current.lines.filter((line) => line.row_key !== rowKey);
      return {
        ...current,
        lines: remaining.length >= 2 ? remaining : [...remaining, makeLine()],
      };
    });
  };

  const buildPayload = () => ({
    ...companyParams,
    transaction_date: form.transaction_date,
    fiscal_year: form.fiscal_year,
    reference_no: form.reference_no,
    description: form.description,
    currency_code: form.currency_code,
    version: form.version,
    lines: form.lines
      .map((line) => {
        const customer = customers.find(
          (c) => String(c._id) === String(line.customer_id)
        );
        const supplier = suppliers.find(
          (s) => String(s._id) === String(line.supplier_id)
        );
        const accountId =
          customer?.customer_account?._id ||
          supplier?.supplier_account?._id ||
          line.account_id;
        return {
          account_id: accountId || undefined,
          customer_id: line.customer_id || undefined,
          supplier_id: line.supplier_id || undefined,
          debit: Number(line.debit || 0),
          credit: Number(line.credit || 0),
          description: line.description || "",
        };
      })
      .filter((line) => line.account_id),
  });

  const validateDuplicates = (lines) => {
    const customerIds = lines
      .filter(line => line.customer_id)
      .map(line => line.customer_id);
    const supplierIds = lines
      .filter(line => line.supplier_id)
      .map(line => line.supplier_id);

    const customerSet = new Set();
    for (const id of customerIds) {
      if (customerSet.has(id)) {
        const customer = customers.find(c => String(c._id) === String(id));
        return `Customer "${customer?.customer_name || id}" appears in multiple lines.`;
      }
      customerSet.add(id);
    }

    const supplierSet = new Set();
    for (const id of supplierIds) {
      if (supplierSet.has(id)) {
        const supplier = suppliers.find(s => String(s._id) === String(id));
        return `Supplier "${supplier?.supplier_name || id}" appears in multiple lines.`;
      }
      supplierSet.add(id);
    }

    return null;
  };

  const applyTransaction = (transaction) => {
    const loadedLines = (transaction.lines || []).map((line) => {
      const accountId =
        typeof line.account_id === "object"
          ? line.account_id?._id || ""
          : line.account_id || "";
      const customerId =
        typeof line.customer_id === "object"
          ? line.customer_id?._id || ""
          : line.customer_id || "";
      const supplierId =
        typeof line.supplier_id === "object"
          ? line.supplier_id?._id || ""
          : line.supplier_id || "";
      const debitAmount = Number(line.debit || 0);
      const creditAmount = Number(line.credit || 0);
      return {
        row_key:
          line._id ||
          `${Date.now()}-${line.line_no}-${Math.random()
            .toString(36)
            .slice(2)}`,
        account_id: accountId,
        customer_id: customerId,
        supplier_id: supplierId,
        debit:
          debitAmount > 0
            ? String(debitAmount)
            : "",
        credit:
          creditAmount > 0
            ? String(creditAmount)
            : "",
        description: line.description || "",
      };
    });

    while (loadedLines.length < 2) {
      loadedLines.push(makeLine());
    }

    setForm({
      _id: transaction._id || null,
      version: transaction.version || 1,
      transaction_no: transaction.transaction_no || "",
      transaction_date: String(
        transaction.transaction_date || today(),
      ).slice(0, 10),
      fiscal_year:
        transaction.fiscal_year || currentFiscalYear(),
      reference_no:
        transaction.reference_no ||
        transaction.transaction_no ||
        "",
      description: transaction.description || "",
      currency_code:
        transaction.currency_code || companyCurrency,
      status:
        transaction.status || "draft",
      lines: loadedLines,
    });
  };

  const saveDraft = async ({ silent = false, manageSaving = true } = {}) => {
    const duplicateError = validateDuplicates(form.lines);
    if (duplicateError) {
      showMessage("error", duplicateError);
      return null;
    }

    if (manageSaving) setSaving(true);
    try {
      const payload = buildPayload();
      const response = form._id
        ? await axios.put(
            `${url}/api/accounting-opening-balances/${form._id}`,
            payload,
            { headers: getAuthHeaders() }
          )
        : await axios.post(
            `${url}/api/accounting-opening-balances`,
            payload,
            { headers: getAuthHeaders() }
          );
      const transaction = response.data?.data;
      if (transaction) applyTransaction(transaction);
      await refreshList();
      if (!silent) {
        showMessage("success", response.data?.message || "Draft saved");
      }
      return transaction;
    } catch (error) {
      showMessage("error", unwrapError(error, "Failed to save draft"));
      return null;
    } finally {
      if (manageSaving) setSaving(false);
    }
  };

  const postEntry = async () => {
    if (!isBalanced) {
      showMessage("error", "Total debit must equal total credit before posting");
      return;
    }
    if (!window.confirm("Post this beginning balance? Posted entries are locked.")) {
      return;
    }
    setSaving(true);
    try {
      const transaction = await saveDraft({ silent: true, manageSaving: false });
      if (!transaction?._id) return;
      const transactionId = transaction._id;
      const response = await axios.post(
        `${url}/api/accounting-opening-balances/${transactionId}/post`,
        companyParams,
        { headers: getAuthHeaders() }
      );
      applyTransaction(response.data?.data);
      await Promise.all([refreshList(), loadMetadata()]);
      showMessage("success", response.data?.message || "Beginning balances posted");
    } catch (error) {
      showMessage("error", unwrapError(error, "Failed to post beginning balances"));
    } finally {
      setSaving(false);
    }
  };

  const deleteDraft = async () => {
    if (!form._id || !isDraft) return;
    if (!window.confirm(`Delete draft ${form.transaction_no}?`)) return;
    setSaving(true);
    try {
      const response = await axios.delete(
        `${url}/api/accounting-opening-balances/${form._id}`,
        {
          data: companyParams,
          headers: getAuthHeaders(),
        }
      );
      await Promise.all([newEntry(), refreshList()]);
      showMessage("success", response.data?.message || "Draft deleted");
    } catch (error) {
      showMessage("error", unwrapError(error, "Failed to delete draft"));
    } finally {
      setSaving(false);
    }
  };

  const reverseEntry = async () => {
    if (!form._id || !isPosted) return;
    const reason = window.prompt(
      `Reason for reversing ${form.transaction_no}:`,
      "Opening balance correction",
    );
    if (reason === null) return;
    setSaving(true);
    try {
      const response = await axios.post(
        `${url}/api/accounting-opening-balances/${form._id}/reverse`,
        { ...companyParams, reason },
        { headers: getAuthHeaders() }
      );
      await Promise.all([newEntry(), refreshList(), loadMetadata()]);
      showMessage("success", response.data?.message || "Entry reversed");
    } catch (error) {
      showMessage("error", unwrapError(error, "Failed to reverse entry"));
    } finally {
      setSaving(false);
    }
  };

  const openEntry = async (entryId) => {
    setSaving(true);
    try {
      const response = await axios.get(
        `${url}/api/accounting-opening-balances/${entryId}`,
        {
          params: companyParams,
          headers: getAuthHeaders(),
        }
      );
      applyTransaction(response.data?.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      showMessage("error", unwrapError(error, "Failed to open entry"));
    } finally {
      setSaving(false);
    }
  };

  const statusLabel = (status) =>
    status ? status.charAt(0).toUpperCase() + status.slice(1) : "Draft";

  // ======================== CUSTOMER MODAL HANDLERS ========================
  const openAddCustomerModal = (rowKey) => {
    setCustomerModalLineKey(rowKey);
    setNewCustomerForm({
      customer_name: "",
      customer_type: "regular",
      phone: "",
      email: "",
      address: "",
      currency_code: companyCurrency,
      credit_limit: "0.00",
    });
    setCustomerErrors({});
    setShowCustomerModal(true);
  };

  const handleCustomerFormChange = (field, value) => {
    setNewCustomerForm((prev) => ({ ...prev, [field]: value }));
    setCustomerErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateCustomerForm = () => {
    const errors = {};
    const name = newCustomerForm.customer_name.trim();
    if (name.length < 2 || name.length > 100) {
      errors.customer_name = "Name must be 2-100 characters";
    }
    if (newCustomerForm.phone && !/^[\d\s+()\-]{7,30}$/.test(newCustomerForm.phone.trim())) {
      errors.phone = "Enter a valid phone number";
    }
    if (
      newCustomerForm.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newCustomerForm.email.trim())
    ) {
      errors.email = "Enter a valid email address";
    }
    if (!/^[A-Za-z]{3}$/.test(newCustomerForm.currency_code)) {
      errors.currency_code = "Use a three-letter currency code";
    }
    const creditLimit = Number(newCustomerForm.credit_limit || 0);
    if (!Number.isFinite(creditLimit) || creditLimit < 0) {
      errors.credit_limit = "Credit limit must be zero or greater";
    }
    setCustomerErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const createCustomerAndSelect = async () => {
    if (!validateCustomerForm()) return;
    setAddingCustomer(true);
    try {
      const payload = {
        customer_name: newCustomerForm.customer_name.trim(),
        customer_type: newCustomerForm.customer_type,
        phone: newCustomerForm.phone.trim(),
        email: newCustomerForm.email.trim().toLowerCase(),
        address: newCustomerForm.address.trim(),
        currency_code: newCustomerForm.currency_code.trim().toUpperCase(),
        credit_limit:
          newCustomerForm.customer_type === "walk_in"
            ? 0
            : Number(newCustomerForm.credit_limit || 0),
        company_id: companyId,
      };
      const response = await axios.post(`${url}/api/customer`, payload, {
        headers: getAuthHeaders(),
      });
      const newCustomer = response.data?.data;
      if (newCustomer) {
        await loadCustomers();
        if (customerModalLineKey) {
          changeLine(customerModalLineKey, "customer_id", newCustomer._id);
        }
        setShowCustomerModal(false);
        setCustomerModalLineKey(null);
        showMessage("success", `Customer "${newCustomer.customer_name}" created`);
      }
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to create customer";
      showMessage("error", msg);
      if (error.response?.data?.errors) {
        const fieldErrors = {};
        error.response.data.errors.forEach((err) => {
          fieldErrors[err.field] = err.message;
        });
        setCustomerErrors(fieldErrors);
      }
    } finally {
      setAddingCustomer(false);
    }
  };

  const closeCustomerModal = () => {
    setShowCustomerModal(false);
    setCustomerModalLineKey(null);
    setCustomerErrors({});
  };

  // ======================== SUPPLIER MODAL HANDLERS ========================
  const openAddSupplierModal = (rowKey) => {
    setSupplierModalLineKey(rowKey);
    setNewSupplierForm({
      supplier_name: "",
      phone: "",
      email: "",
      address: "",
      currency_code: companyCurrency,
      credit_limit: "0.00",
    });
    setSupplierErrors({});
    setShowSupplierModal(true);
  };

  const handleSupplierFormChange = (field, value) => {
    setNewSupplierForm((prev) => ({ ...prev, [field]: value }));
    setSupplierErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateSupplierForm = () => {
    const errors = {};
    const name = newSupplierForm.supplier_name.trim();
    if (name.length < 2 || name.length > 100) {
      errors.supplier_name = "Name must be 2-100 characters";
    }
    if (newSupplierForm.phone && !/^[\d\s+()\-]{7,30}$/.test(newSupplierForm.phone.trim())) {
      errors.phone = "Enter a valid phone number";
    }
    if (
      newSupplierForm.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newSupplierForm.email.trim())
    ) {
      errors.email = "Enter a valid email address";
    }
    if (!/^[A-Za-z]{3}$/.test(newSupplierForm.currency_code)) {
      errors.currency_code = "Use a three-letter currency code";
    }
    const creditLimit = Number(newSupplierForm.credit_limit || 0);
    if (!Number.isFinite(creditLimit) || creditLimit < 0) {
      errors.credit_limit = "Credit limit must be zero or greater";
    }
    setSupplierErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const createSupplierAndSelect = async () => {
    if (!validateSupplierForm()) return;
    setAddingSupplier(true);
    try {
      const payload = {
        supplier_name: newSupplierForm.supplier_name.trim(),
        phone: newSupplierForm.phone.trim(),
        email: newSupplierForm.email.trim().toLowerCase(),
        currency_code: newSupplierForm.currency_code.trim().toUpperCase(),
        credit_limit: Number(newSupplierForm.credit_limit || 0),
        company_id: companyId,
      };
      const response = await axios.post(`${url}/api/supplier`, payload, {
        headers: getAuthHeaders(),
      });
      const newSupplier = response.data?.data;
      if (newSupplier) {
        await loadSuppliers();
        if (supplierModalLineKey) {
          changeLine(supplierModalLineKey, "supplier_id", newSupplier._id);
        }
        setShowSupplierModal(false);
        setSupplierModalLineKey(null);
        showMessage("success", `Supplier "${newSupplier.supplier_name}" created`);
      }
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to create supplier";
      showMessage("error", msg);
      if (error.response?.data?.errors) {
        const fieldErrors = {};
        error.response.data.errors.forEach((err) => {
          fieldErrors[err.field] = err.message;
        });
        setSupplierErrors(fieldErrors);
      }
    } finally {
      setAddingSupplier(false);
    }
  };

  const closeSupplierModal = () => {
    setShowSupplierModal(false);
    setSupplierModalLineKey(null);
    setSupplierErrors({});
  };

  if (loading) {
    return (
      <div className="bb-loading">
        <FiRefreshCw className="bb-spin" />
        <span>Loading beginning balances…</span>
      </div>
    );
  }

  return (
    <section className="bb-page">
      {/* ===== HEADER ===== */}
      <header className="bb-page-header">
        <div>
          <p className="bb-eyebrow">ACCOUNTING</p>
          <h1>Beginning Balances</h1>
          <p>
            Enter one balanced opening voucher for the selected fiscal year. Drafts
            can be edited; posted entries are locked and corrected by reversal.
          </p>
        </div>
        <div className="bb-header-actions">
          <button className="bb-button bb-button-secondary" onClick={newEntry} disabled={saving}>
            <FiPlus /> New
          </button>
          <button
            className="bb-button bb-button-secondary"
            onClick={() => saveDraft()}
            disabled={saving || !isDraft}
          >
            <FiSave /> Save draft
          </button>
          <button
            className="bb-button bb-button-primary"
            onClick={postEntry}
            disabled={saving || !isDraft || !isBalanced}
          >
            <FiUploadCloud /> Post
          </button>
          {isDraft && form._id ? (
            <button className="bb-button bb-button-danger" onClick={deleteDraft} disabled={saving}>
              <FiTrash2 /> Delete
            </button>
          ) : null}
          {isPosted ? (
            <button className="bb-button bb-button-warning" onClick={reverseEntry} disabled={saving}>
              <FiRotateCcw /> Reverse
            </button>
          ) : null}
        </div>
      </header>

      {/* ===== ALERTS ===== */}
      {message ? (
        <div className={`bb-alert bb-alert-${message.type}`}>
          {message.type === "success" ? <FiCheckCircle /> : <FiAlertCircle />}
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)} aria-label="Close message">
            <FiX />
          </button>
        </div>
      ) : null}

      {/* ===== SUMMARY ===== */}
      <div className="bb-summary-grid">
        <article className="bb-summary-card">
          <span>Total entries</span>
          <strong>{summary.total}</strong>
          <small>All beginning balance vouchers</small>
        </article>
        <article className="bb-summary-card">
          <span>Draft</span>
          <strong>{summary.draft}</strong>
          <small>Editable and not posted</small>
        </article>
        <article className="bb-summary-card">
          <span>Posted</span>
          <strong>{summary.posted}</strong>
          <small>Included in account balances</small>
        </article>
        <article className="bb-summary-card">
          <span>Current difference</span>
          <strong className={isBalanced ? "bb-positive" : difference ? "bb-negative" : ""}>
            {formatAmount(Math.abs(difference))}
          </strong>
          <small>{isBalanced ? "Voucher is balanced" : "Debit minus credit"}</small>
        </article>
      </div>

      {/* ===== ENTRY FORM ===== */}
      <div className="bb-entry-card">
        <div className="bb-entry-title-row">
          <div>
            <h2>Beginning Balance Entry</h2>
            <span className={`bb-status bb-status-${form.status}`}>
              {statusLabel(form.status)}
            </span>
          </div>
          <strong>{form.transaction_no || "New voucher"}</strong>
        </div>

        <div className="bb-header-form">
          <label>
            <span>Entry date *</span>
            <input
              type="date"
              value={form.transaction_date}
              onChange={(event) => changeHeader("transaction_date", event.target.value)}
              disabled={!isDraft || saving}
            />
          </label>
          <label>
            <span>Reference No. *</span>
            <input
              value={form.reference_no}
              onChange={(event) => changeHeader("reference_no", event.target.value.toUpperCase())}
              disabled={!isDraft || saving}
              placeholder="BB-0001"
            />
          </label>
          <label>
            <span>Fiscal year *</span>
            <input
              list="bb-fiscal-years"
              value={form.fiscal_year}
              onChange={(event) => changeHeader("fiscal_year", event.target.value)}
              disabled={!isDraft || saving}
              placeholder="2026-2027"
            />
            <datalist id="bb-fiscal-years">
              {fiscalYears.map((year) => (
                <option key={year} value={year} />
              ))}
            </datalist>
          </label>
          <label>
            <span>Currency</span>
            <input
              value={form.currency_code}
              maxLength={3}
              onChange={(event) => changeHeader("currency_code", event.target.value.toUpperCase())}
              disabled={!isDraft || saving}
            />
          </label>
          <label className="bb-description-field">
            <span>Description *</span>
            <input
              value={form.description}
              onChange={(event) => changeHeader("description", event.target.value)}
              disabled={!isDraft || saving}
              placeholder="Opening balances as at the conversion date"
            />
          </label>
        </div>

        {/* ===== TABLE ===== */}
        <div className="bb-table-wrap">
          <table className="bb-entry-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Account *</th>
                <th>Account name</th>
                <th>Normal</th>
                <th>Customer / Supplier</th>
                <th>Debit (DR)</th>
                <th>Credit (CR)</th>
                <th>Line description</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {form.lines.map((line, index) => {
                const account = accountMap.get(String(line.account_id));
                const accountingType = getAccountType(account);
                const normalBalance = getNormalBalance(account);
                const isAR = account?.system_account_role === "ACCOUNTS_RECEIVABLE";
                const isAP = account?.system_account_role === "ACCOUNTS_PAYABLE";

                const selectedCustomerIds = new Set(
                  form.lines
                    .filter((otherLine) => otherLine.row_key !== line.row_key)
                    .filter((otherLine) => {
                      const acc = accountMap.get(String(otherLine.account_id));
                      return acc && acc.system_account_role === "ACCOUNTS_RECEIVABLE";
                    })
                    .map((otherLine) => otherLine.customer_id)
                    .filter(Boolean)
                );
                const selectedSupplierIds = new Set(
                  form.lines
                    .filter((otherLine) => otherLine.row_key !== line.row_key)
                    .filter((otherLine) => {
                      const acc = accountMap.get(String(otherLine.account_id));
                      return acc && acc.system_account_role === "ACCOUNTS_PAYABLE";
                    })
                    .map((otherLine) => otherLine.supplier_id)
                    .filter(Boolean)
                );

                const hasDebit = Number(line.debit) > 0;
                const hasCredit = Number(line.credit) > 0;
                const debitAllowed = !hasCredit;
                const creditAllowed = !hasDebit;

                const unavailableAccountIds = new Set(
                  form.lines
                    .filter((otherLine) => otherLine.row_key !== line.row_key)
                    .filter((otherLine) => {
                      const otherAccount = accountMap.get(String(otherLine.account_id));
                      if (otherAccount && !(otherAccount.system_account_role === "ACCOUNTS_RECEIVABLE" || otherAccount.system_account_role === "ACCOUNTS_PAYABLE")) {
                        return true;
                      }
                      return false;
                    })
                    .map((otherLine) => String(otherLine.account_id || ""))
                    .filter(Boolean),
                );

                return (
                  <tr key={line.row_key}>
                    <td className="bb-line-number">{index + 1}</td>
                    <td>
                      <AccountCombobox
                        value={line.account_id}
                        accounts={generalLedgerAccounts}
                        unavailableIds={unavailableAccountIds}
                        disabled={!isDraft || saving}
                        listId={`bb-account-options-${line.row_key.replace(/[^a-zA-Z0-9_-]/g, "")}`}
                        onChange={(accountId) =>
                          changeLine(line.row_key, "account_id", accountId)
                        }
                      />
                    </td>
                    <td>
                      <div className="bb-account-name">
                        <strong>{account?.account_name || "—"}</strong>
                        <small>
                          {account
                            ? `${accountingType.type || account.type} / ${accountingType.sub_type || account.type}`
                            : "Choose a posting account"}
                        </small>
                      </div>
                    </td>
                    <td>
                      <span className="bb-normal-badge">{account ? normalBalance : "—"}</span>
                    </td>
                    <td>
                      {isAR ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          <CustomerCombobox
                            value={line.customer_id}
                            customers={customers}
                            excludedIds={selectedCustomerIds}
                            disabled={!isDraft || saving}
                            listId={`bb-customer-options-${line.row_key.replace(/[^a-zA-Z0-9_-]/g, "")}`}
                            onChange={(customerId) =>
                              changeLine(line.row_key, "customer_id", customerId)
                            }
                          />
                          <button
                            type="button"
                            className="bb-add-customer-btn"
                            onClick={() => openAddCustomerModal(line.row_key)}
                            disabled={!isDraft || saving}
                            title="Add new customer"
                          >
                            <FiPlus size={14} />
                          </button>
                        </div>
                      ) : isAP ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          <SupplierCombobox
                            value={line.supplier_id}
                            suppliers={suppliers}
                            excludedIds={selectedSupplierIds}
                            disabled={!isDraft || saving}
                            listId={`bb-supplier-options-${line.row_key.replace(/[^a-zA-Z0-9_-]/g, "")}`}
                            onChange={(supplierId) =>
                              changeLine(line.row_key, "supplier_id", supplierId)
                            }
                          />
                          <button
                            type="button"
                            className="bb-add-supplier-btn"
                            onClick={() => openAddSupplierModal(line.row_key)}
                            disabled={!isDraft || saving}
                            title="Add new supplier"
                          >
                            <FiPlus size={14} />
                          </button>
                        </div>
                      ) : (
                        <span style={{ color: "#999" }}>—</span>
                      )}
                    </td>
                    <td>
                      <input
                        className="bb-money-input"
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.debit}
                        onChange={(event) =>
                          changeLine(
                            line.row_key,
                            "debit",
                            event.target.value,
                          )
                        }
                        disabled={
                          !isDraft ||
                          saving ||
                          !debitAllowed
                        }
                        placeholder="0.00"
                        title={
                          hasCredit
                            ? "Clear the credit amount first"
                            : "Debit amount"
                        }
                      />
                    </td>
                    <td>
                      <input
                        className="bb-money-input"
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.credit}
                        onChange={(event) =>
                          changeLine(
                            line.row_key,
                            "credit",
                            event.target.value,
                          )
                        }
                        disabled={
                          !isDraft ||
                          saving ||
                          !creditAllowed
                        }
                        placeholder="0.00"
                        title={
                          hasDebit
                            ? "Clear the debit amount first"
                            : "Credit amount"
                        }
                      />
                    </td>
                    <td>
                      <input
                        value={line.description}
                        onChange={(event) => changeLine(line.row_key, "description", event.target.value)}
                        disabled={!isDraft || saving}
                        placeholder="Optional line note"
                      />
                    </td>
                    <td>
                      <button
                        className="bb-icon-button bb-icon-danger"
                        onClick={() => removeLine(line.row_key)}
                        disabled={!isDraft || saving || form.lines.length <= 2}
                        title="Remove line"
                        type="button"
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="5">
                  <button
                    className="bb-add-line"
                    type="button"
                    onClick={addLine}
                    disabled={!isDraft || saving}
                  >
                    <FiPlus /> Add line
                  </button>
                </td>
                <td>{formatAmount(totals.debit)}</td>
                <td>{formatAmount(totals.credit)}</td>
                <td colSpan="2">
                  <span className={isBalanced ? "bb-balance-ok" : "bb-balance-warning"}>
                    {isBalanced ? <FiCheckCircle /> : <FiAlertCircle />}
                    {isBalanced
                      ? "Balanced"
                      : `Difference ${formatAmount(Math.abs(difference))}`}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="bb-note">
          <FiAlertCircle />
          <div>
            <strong>Posting rule</strong>
            <p>
              Use subaccounts for specific cash, bank, wallet and owner balances.
              For Accounts Receivable, select a customer. For Accounts Payable, select a supplier.
              Every line must contain either a debit or credit, and total debit must equal total credit.
              A customer or supplier can only appear once per voucher.
            </p>
          </div>
        </div>
      </div>

      {/* ===== HISTORY LIST ===== */}
      <div className="bb-list-card">
        <div className="bb-list-toolbar">
          <div>
            <h2>Beginning Balance History</h2>
            <p>Open a draft to continue editing or review a posted voucher.</p>
          </div>
          <div className="bb-list-filters">
            <label className="bb-search-box">
              <FiSearch />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search voucher, fiscal year or description"
              />
            </label>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="all">All statuses</option>
              <option value="draft">Draft</option>
              <option value="posted">Posted</option>
              <option value="reversed">Reversed</option>
            </select>
            <button className="bb-icon-button" onClick={refreshList} title="Refresh">
              <FiRefreshCw />
            </button>
          </div>
        </div>

        <div className="bb-table-wrap">
          <table className="bb-history-table">
            <thead>
              <tr>
                <th>Voucher</th>
                <th>Date</th>
                <th>Fiscal year</th>
                <th>Description</th>
                <th>Debit</th>
                <th>Credit</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {entries.length ? (
                entries.map((entry) => (
                  <tr key={entry._id}>
                    <td>
                      <strong>{entry.transaction_no}</strong>
                      <small>{entry.reference_no || "—"}</small>
                    </td>
                    <td>{String(entry.transaction_date).slice(0, 10)}</td>
                    <td>{entry.fiscal_year}</td>
                    <td>{entry.description}</td>
                    <td>{formatAmount(entry.total_debit || 0)}</td>
                    <td>{formatAmount(entry.total_credit || 0)}</td>
                    <td>
                      <span className={`bb-status bb-status-${entry.status}`}>
                        {statusLabel(entry.status)}
                      </span>
                    </td>
                    <td>
                      <button className="bb-open-button" onClick={() => openEntry(entry._id)}>
                        <FiFileText /> Open
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="bb-empty-state">
                    <FiClock />
                    <strong>No beginning balance entries found</strong>
                    <span>Create a new draft to begin.</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ======================== CUSTOMER ADD MODAL ======================== */}
      {showCustomerModal && (
        <div className="bb-modal-overlay" role="presentation" onMouseDown={closeCustomerModal}>
          <div
            className="bb-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Add new customer"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="bb-modal-header">
              <h3>Add New Customer</h3>
              <button className="bb-modal-close" onClick={closeCustomerModal}>
                <FiX />
              </button>
            </div>
            <div className="bb-modal-body">
              <div className="bb-customer-form">
                <label>
                  Customer name *
                  <input
                    value={newCustomerForm.customer_name}
                    onChange={(e) => handleCustomerFormChange("customer_name", e.target.value)}
                    placeholder="Enter customer name"
                  />
                  {customerErrors.customer_name && <span className="bb-error">{customerErrors.customer_name}</span>}
                </label>
                <label>
                  Customer type
                  <select
                    value={newCustomerForm.customer_type}
                    onChange={(e) => handleCustomerFormChange("customer_type", e.target.value)}
                  >
                    <option value="walk_in">Walk In</option>
                    <option value="regular">Regular</option>
                    <option value="credit">Credit</option>
                  </select>
                </label>
                <label>
                  Phone
                  <input
                    value={newCustomerForm.phone}
                    onChange={(e) => handleCustomerFormChange("phone", e.target.value)}
                    placeholder="Phone number"
                  />
                  {customerErrors.phone && <span className="bb-error">{customerErrors.phone}</span>}
                </label>
                <label>
                  Email
                  <input
                    type="email"
                    value={newCustomerForm.email}
                    onChange={(e) => handleCustomerFormChange("email", e.target.value)}
                    placeholder="Email address"
                  />
                  {customerErrors.email && <span className="bb-error">{customerErrors.email}</span>}
                </label>
                <label>
                  Address
                  <input
                    value={newCustomerForm.address}
                    onChange={(e) => handleCustomerFormChange("address", e.target.value)}
                    placeholder="Address"
                  />
                </label>
                <label>
                  Currency
                  <input
                    value={newCustomerForm.currency_code}
                    onChange={(e) => handleCustomerFormChange("currency_code", e.target.value.toUpperCase())}
                    maxLength={3}
                    placeholder="USD"
                  />
                  {customerErrors.currency_code && <span className="bb-error">{customerErrors.currency_code}</span>}
                </label>
                <label>
                  Credit limit
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newCustomerForm.credit_limit}
                    onChange={(e) => handleCustomerFormChange("credit_limit", e.target.value)}
                    disabled={newCustomerForm.customer_type === "walk_in"}
                  />
                  {customerErrors.credit_limit && <span className="bb-error">{customerErrors.credit_limit}</span>}
                </label>
              </div>
            </div>
            <div className="bb-modal-footer">
              <button className="bb-button bb-button-secondary" onClick={closeCustomerModal} disabled={addingCustomer}>
                Cancel
              </button>
              <button
                className="bb-button bb-button-primary"
                onClick={createCustomerAndSelect}
                disabled={addingCustomer}
              >
                {addingCustomer ? "Creating..." : "Create & Select"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================== SUPPLIER ADD MODAL ======================== */}
      {showSupplierModal && (
        <div className="bb-modal-overlay" role="presentation" onMouseDown={closeSupplierModal}>
          <div
            className="bb-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Add new supplier"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="bb-modal-header">
              <h3>Add New Supplier</h3>
              <button className="bb-modal-close" onClick={closeSupplierModal}>
                <FiX />
              </button>
            </div>
            <div className="bb-modal-body">
              <div className="bb-supplier-form">
                <label>
                  Supplier name *
                  <input
                    value={newSupplierForm.supplier_name}
                    onChange={(e) => handleSupplierFormChange("supplier_name", e.target.value)}
                    placeholder="Enter supplier name"
                  />
                  {supplierErrors.supplier_name && <span className="bb-error">{supplierErrors.supplier_name}</span>}
                </label>
                <label>
                  Phone
                  <input
                    value={newSupplierForm.phone}
                    onChange={(e) => handleSupplierFormChange("phone", e.target.value)}
                    placeholder="Phone number"
                  />
                  {supplierErrors.phone && <span className="bb-error">{supplierErrors.phone}</span>}
                </label>
                <label>
                  Email
                  <input
                    type="email"
                    value={newSupplierForm.email}
                    onChange={(e) => handleSupplierFormChange("email", e.target.value)}
                    placeholder="Email address"
                  />
                  {supplierErrors.email && <span className="bb-error">{supplierErrors.email}</span>}
                </label>
                <label>
                  Address
                  <input
                    value={newSupplierForm.address}
                    onChange={(e) => handleSupplierFormChange("address", e.target.value)}
                    placeholder="Address"
                  />
                </label>
                <label>
                  Currency
                  <input
                    value={newSupplierForm.currency_code}
                    onChange={(e) => handleSupplierFormChange("currency_code", e.target.value.toUpperCase())}
                    maxLength={3}
                    placeholder="USD"
                  />
                  {supplierErrors.currency_code && <span className="bb-error">{supplierErrors.currency_code}</span>}
                </label>
                <label>
                  Credit limit
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newSupplierForm.credit_limit}
                    onChange={(e) => handleSupplierFormChange("credit_limit", e.target.value)}
                  />
                  {supplierErrors.credit_limit && <span className="bb-error">{supplierErrors.credit_limit}</span>}
                </label>
              </div>
            </div>
            <div className="bb-modal-footer">
              <button className="bb-button bb-button-secondary" onClick={closeSupplierModal} disabled={addingSupplier}>
                Cancel
              </button>
              <button
                className="bb-button bb-button-primary"
                onClick={createSupplierAndSelect}
                disabled={addingSupplier}
              >
                {addingSupplier ? "Creating..." : "Create & Select"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default BeginningBalances;